/**
 * components/inspection/CriticalControlSheet.js
 * «ЧУХАЛ ХЯНАЛТЫН ШАЛГАХ ХУУДАС» — үлгэрчилсэн загварын дагуу.
 *
 * Эх сурвалж: `чухал хяналт/Чухал хяналт.vsdx` → «Чухал хяналтын шалгах
 * хуудасны үлгэрчилсэн загвар» ба `Чухал хяналтын процесс.vsdx` урсгал:
 *
 *   Ажилтан / АШУА → ажлын даалгавар авна
 *     → ӨНДӨР ЭРСДЭЛТЭЙ ажил гүйцэтгэх үед чухал хяналтын хуудас бөглөнө
 *     → хэвийн бол ажлаа гүйцэтгэнэ
 *     → ажил хийх боломжгүй бол АЖИЛ ЗОГСООХ ЭРХ эдэлнэ
 *     → ХАБЭА-н ажилтан газар дээр нь очиж танилцаж, ХАБЭАХ судална
 *     → арга хэмжээ авч ажилтныг ажилд оруулна (ҮЛ БУРУУТГАХ зарчим)
 *
 * Хуудасны бүтэц (загвартай яг ижил):
 *   № | Хяналтын асуумж | Наряд зөвшөөрөлд тусгасан эсэх
 *     | Эрчим хүч тусгаарласан эсэх (цоож, пайз г.м)
 *     | Өндөрт ажиллах аюулгүй байдлыг бүрэн хангасан эсэх (Шат, тавцан, бүс г.м)
 *     | АЖЛЫГ ЗОГСООХ ЭРХ  (улаан багана)
 *
 * 15 асуумж нь лавлахаас (checklistStore "CRITICAL") ирнэ — DB дээр засвал
 * энд шууд тусна. Татагдахгүй бол built-in seed ашиглагдана.
 *
 * Хэрэглэгдэх газрууд:
 *   • /inspection/register — зөрчлийн маягт → Үзлэгийн төрөл = «Чухал хяналт»
 *   • Өндөр эрсдэлтэй ажлын наряд зөвшөөрөл (ӨЭА) — ажил эхлэхийн өмнө
 */
import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Input, DatePicker, Alert, Tag } from "antd";
import moment from "moment";

import {
  loadChecklist,
  listChecklist,
} from "src/pages/risk/reference/checklistStore";

const RED = "#C62828";
const GREEN = "#16A34A";

// Загварын 3 хяналтын багана. `key` нь хариултын объектод хадгалагдана.
export const CONTROL_COLUMNS = [
  {
    key: "naryad",
    title: "Наряд зөвшөөрөлд тусгасан эсэх",
  },
  {
    key: "energy",
    title: "Эрчим хүч тусгаарласан эсэх",
    hint: "(цоож, пайз г.м)",
  },
  {
    key: "height",
    title: "Өндөрт ажиллах аюулгүй байдлыг бүрэн хангасан эсэх",
    hint: "(шат, тавцан, бүс г.м)",
  },
];

/** Хоосон хариулт — асуумж бүрд 3 хяналт + ажил зогсоох эрх. */
const emptyAnswers = (items) => {
  const a = {};
  items.forEach((it) => {
    a[it.no] = { naryad: false, energy: false, height: false, stop: false };
  });
  return a;
};

/** Хариултаас тоон дүн — бөглөсөн, зогсоох эрх эдэлсэн асуумжийн тоо. */
export const summarizeCriticalSheet = (answers) => {
  const rows = Object.values(answers || {});
  const checked = rows.filter(
    (r) => r.naryad || r.energy || r.height || r.stop,
  ).length;
  const stops = rows.filter((r) => r.stop).length;
  return { total: rows.length, checked, stops, canWork: stops === 0 };
};

function Box({ checked, onChange, danger }) {
  return (
    <span
      role="checkbox"
      aria-checked={checked}
      tabIndex={0}
      onClick={() => onChange(!checked)}
      onKeyDown={(e) => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          onChange(!checked);
        }
      }}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 22,
        height: 22,
        borderRadius: 4,
        cursor: "pointer",
        userSelect: "none",
        border: "1.5px solid " + (danger ? "#fff" : "#8A94A6"),
        background: checked
          ? danger
            ? "#fff"
            : GREEN
          : danger
            ? "rgba(255,255,255,0.25)"
            : "#fff",
        color: checked ? (danger ? RED : "#fff") : "transparent",
        fontSize: 13,
        fontWeight: 900,
        lineHeight: 1,
      }}
    >
      ✓
    </span>
  );
}

const th = {
  border: "1px solid #B9C0CC",
  padding: "6px 8px",
  fontSize: 11.5,
  fontWeight: 700,
  color: "#1F2430",
  background: "#F3F5F8",
  textAlign: "center",
  verticalAlign: "middle",
};
const td = {
  border: "1px solid #B9C0CC",
  padding: "6px 8px",
  fontSize: 12.5,
  color: "#1F2430",
  verticalAlign: "middle",
};

export default function CriticalControlSheet({
  open,
  onClose,
  onSubmit,
  /** Толгойд харуулах хам сэдэв — ажил, нэгж, ажилтан */
  context = {},
  /** Өмнө бөглөсөн утга (засах үед) */
  value,
  /** true бол зөвхөн харах (засах боломжгүй) */
  readOnly = false,
}) {
  const [items, setItems] = useState(() => listChecklist("CRITICAL"));
  const [answers, setAnswers] = useState({});
  const [date, setDate] = useState(() => moment());
  const [note, setNote] = useState("");

  // Лавлахыг DB-ээс шинэчилнэ (кэшээс шууд харуулж, дараа нь солино)
  useEffect(() => {
    if (!open) return;
    loadChecklist("CRITICAL").then((rows) => {
      if (Array.isArray(rows) && rows.length) setItems(rows);
    });
  }, [open]);

  // Цонх нээгдэх бүрд эхлэх төлөв
  useEffect(() => {
    if (!open) return;
    setAnswers(value?.answers || emptyAnswers(items));
    setNote(value?.note || "");
    setDate(value?.date ? moment(value.date) : moment());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Лавлах хожуу ирвэл дутуу мөрийг нөхнө
  useEffect(() => {
    setAnswers((prev) => {
      const next = { ...emptyAnswers(items), ...prev };
      return next;
    });
  }, [items]);

  const sum = useMemo(() => summarizeCriticalSheet(answers), [answers]);

  const setCell = (no, key, val) =>
    setAnswers((prev) => ({
      ...prev,
      [no]: { ...(prev[no] || {}), [key]: val },
    }));

  const submit = () => {
    onSubmit?.({
      date: date ? date.format("YYYY-MM-DD") : null,
      answers,
      note,
      summary: sum,
    });
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width="96%"
      style={{ top: 16, maxWidth: 1100 }}
      destroyOnClose
      title={
        <div>
          <div style={{ fontWeight: 800, fontSize: 15, color: "#1F2430" }}>
            <i className="fas fa-shield-alt" style={{ marginRight: 8, color: RED }} />
            Чухал хяналтын шалгах хуудас
          </div>
          <div style={{ fontSize: 11.5, color: "#8A94A6", marginTop: 2 }}>
            Өндөр эрсдэлтэй ажил гүйцэтгэхийн өмнө бөглөнө
          </div>
        </div>
      }
      footer={
        readOnly ? (
          <Button onClick={onClose}>Хаах</Button>
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontSize: 12, color: "#8A94A6", marginRight: "auto" }}>
              Бөглөсөн: <b>{sum.checked}</b>/{sum.total}
              {sum.stops > 0 && (
                <b style={{ color: RED, marginLeft: 8 }}>
                  · Ажил зогсоох: {sum.stops}
                </b>
              )}
            </span>
            <Button onClick={onClose}>Болих</Button>
            <Button
              type="primary"
              danger={!sum.canWork}
              onClick={submit}
              style={
                sum.canWork
                  ? { background: GREEN, borderColor: GREEN }
                  : undefined
              }
            >
              {sum.canWork ? "Хадгалж, ажлаа гүйцэтгэх" : "Ажил зогсоох эрх эдлэх"}
            </Button>
          </div>
        )
      }
    >
      {/* Хам сэдэв — ямар ажил дээр бөглөж байна */}
      {(context.jobName || context.deptName || context.workerName) && (
        <div
          style={{
            border: "1px solid #E6E9EF",
            borderRadius: 8,
            background: "#FCFDFE",
            padding: "8px 12px",
            marginBottom: 10,
            fontSize: 12.5,
            display: "flex",
            flexWrap: "wrap",
            gap: 14,
          }}
        >
          {context.jobName && (
            <span>
              <b>Ажил:</b> {context.jobName}
            </span>
          )}
          {context.deptName && (
            <span>
              <b>Нэгж:</b> {context.deptName}
            </span>
          )}
          {context.workerName && (
            <span>
              <b>Ажилтан:</b> {context.workerName}
            </span>
          )}
        </div>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 8,
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 700 }}>Огноо:</span>
        <DatePicker
          size="small"
          value={date}
          onChange={setDate}
          format="YYYY-MM-DD"
          allowClear={false}
          disabled={readOnly}
        />
        {sum.stops > 0 ? (
          <Tag color="red">Ажил зогсоох эрх эдэлсэн — {sum.stops}</Tag>
        ) : sum.checked > 0 ? (
          <Tag color="green">Хэвийн — ажлаа гүйцэтгэнэ</Tag>
        ) : null}
      </div>

      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            minWidth: 860,
          }}
        >
          <thead>
            <tr>
              <th style={{ ...th, width: 40 }}>№</th>
              <th style={{ ...th, minWidth: 230, textAlign: "left" }}>
                Хяналтын асуумж
              </th>
              {CONTROL_COLUMNS.map((c) => (
                <th key={c.key} style={{ ...th, width: 150 }}>
                  {c.title}
                  {c.hint && (
                    <div style={{ fontWeight: 400, color: "#8A94A6" }}>
                      {c.hint}
                    </div>
                  )}
                </th>
              ))}
              <th
                style={{
                  ...th,
                  width: 130,
                  background: RED,
                  color: "#fff",
                  border: "1px solid " + RED,
                }}
              >
                Ажлыг зогсоох эрх
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const a = answers[it.no] || {};
              return (
                <tr key={it.id || it.no}>
                  <td style={{ ...td, textAlign: "center", fontWeight: 700 }}>
                    {it.no}
                  </td>
                  <td style={td}>{it.title}</td>
                  {CONTROL_COLUMNS.map((c) => (
                    <td key={c.key} style={{ ...td, textAlign: "center" }}>
                      <Box
                        checked={!!a[c.key]}
                        onChange={(v) => !readOnly && setCell(it.no, c.key, v)}
                      />
                    </td>
                  ))}
                  <td
                    style={{
                      ...td,
                      textAlign: "center",
                      background: RED,
                      border: "1px solid " + RED,
                    }}
                  >
                    <Box
                      danger
                      checked={!!a.stop}
                      onChange={(v) => !readOnly && setCell(it.no, "stop", v)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Ажил зогсоосон бол — журмын дагуух дараагийн алхам */}
      {sum.stops > 0 && (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 12 }}
          message="Ажлыг ЗОГСООНО"
          description={
            <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>
              Аюултай нөхцөл байдал илэрсэн тул ажлаа зогсоож, <b>анхан шатны
              удирдах ажилтандаа</b> нэн даруй мэдэгдэнэ үү. ХАБЭА-н ажилтан
              газар дээр нь очиж танилцаж, ХАБЭАХ-тэй хамт судална.
              <br />
              <b>Ажил зогсоох эрх эдэлсэн ажилтанд ҮЛ БУРУУТГАХ зарчим
              баримтална.</b>
            </div>
          }
        />
      )}

      <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 11.5, fontWeight: 700, color: "#8A94A6" }}>
          Тайлбар / илэрсэн аюултай нөхцөл (заавал биш)
        </div>
        <Input.TextArea
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Жш: 3-р тавцангийн хамгаалалтын хашлага дутуу"
          disabled={readOnly}
        />
      </div>

      {/* Загварын хөлийн уриа — эх хуудсанд байгаагаар */}
      <div
        style={{
          marginTop: 12,
          textAlign: "center",
          fontSize: 12.5,
          fontWeight: 700,
          color: RED,
          lineHeight: 1.6,
        }}
      >
        Хамт олны аюулгүй байдлыг та бүрэн хариуцна!!!
        <br />
        Аюултай нөхцөл байдал илэрсэн даруйд ажлаа ЗОГСООЖ
        <br />
        анхан шатны удирдах ажилтандаа мэдэгдэнэ үү.
      </div>
    </Modal>
  );
}
